import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CameraIcon,
  CheckIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/api';

interface CapturedPhoto {
  id: string;
  dataUrl: string;
  file: File;
}

const MobilePhotoCapture: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [cameraActive, setCameraActive] = useState(false);
  const [capturedPhotos, setCapturedPhotos] = useState<CapturedPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [error, setError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
    } catch (error: any) {
      console.error('Camera error:', error);
      if (error.name === 'NotAllowedError') {
        setError('Camera access denied. Please enable camera permissions.');
      } else {
        setError('Failed to access camera. Please try again.');
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const switchCamera = async () => {
    stopCamera();
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
    // Small delay before starting new camera
    setTimeout(() => startCamera(), 100);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0);

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });

        setCapturedPhotos((prev) => [
          ...prev,
          {
            id: `photo_${Date.now()}`,
            dataUrl,
            file,
          },
        ]);

        toast.success('Photo captured!');
      },
      'image/jpeg',
      0.85
    );
  };

  const removePhoto = (photoId: string) => {
    setCapturedPhotos((prev) => prev.filter((p) => p.id !== photoId));
  };

  const uploadPhotos = async () => {
    if (capturedPhotos.length === 0) {
      toast.error('No photos to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const backendUrl = apiService.getBaseUrl();
    let completed = 0;

    for (const photo of capturedPhotos) {
      try {
        const formData = new FormData();
        formData.append('file', photo.file);

        const response = await fetch(`${backendUrl}/api/v1/vehicles/${id}/vehicle-images`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        completed++;
        setUploadProgress(Math.round((completed / capturedPhotos.length) * 100));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload photo ${completed + 1}`);
      }
    }

    setUploading(false);
    setUploadProgress(0);

    if (completed === capturedPhotos.length) {
      toast.success(`All ${completed} photos uploaded successfully!`);
      setCapturedPhotos([]);
      navigate(`/mobile/vehicle/${id}`);
    } else {
      toast.error(`Uploaded ${completed} of ${capturedPhotos.length} photos`);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <header className="bg-black/80 text-white px-4 py-3 flex items-center justify-between z-10">
        <button onClick={() => navigate(`/mobile/vehicle/${id}`)} className="p-2 -ml-2">
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div className="text-center flex-1">
          <h1 className="font-semibold">Photo Capture</h1>
          <p className="text-xs text-gray-400">{capturedPhotos.length} photos captured</p>
        </div>
        <button
          onClick={switchCamera}
          disabled={!cameraActive}
          className="p-2 -mr-2 disabled:opacity-50"
        >
          <ArrowPathIcon className="h-6 w-6" />
        </button>
      </header>

      {/* Camera View */}
      <div className="flex-1 relative bg-gray-900">
        {cameraActive ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {error ? (
              <div className="text-center p-4">
                <XMarkIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <p className="text-white mb-4">{error}</p>
                <button
                  onClick={startCamera}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="text-center p-4">
                <CameraIcon className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">Tap to start camera</p>
                <button
                  onClick={startCamera}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium"
                >
                  Start Camera
                </button>
              </div>
            )}
          </div>
        )}

        {/* Capture guide overlay */}
        {cameraActive && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-4 border-2 border-white/30 rounded-2xl"></div>
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {capturedPhotos.length > 0 && (
        <div className="bg-black/80 p-2">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {capturedPhotos.map((photo) => (
              <div key={photo.id} className="relative flex-shrink-0">
                <img
                  src={photo.dataUrl}
                  alt="Captured"
                  className="h-16 w-16 rounded-lg object-cover"
                />
                <button
                  onClick={() => removePhoto(photo.id)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-black p-4 safe-area-pb">
        {uploading ? (
          <div className="text-center">
            <p className="text-white mb-2">Uploading photos...</p>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-gray-400 text-sm mt-2">{uploadProgress}%</p>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            {/* Gallery button */}
            <button onClick={() => navigate(`/mobile/vehicle/${id}`)} className="p-3 text-white">
              <PhotoIcon className="h-8 w-8" />
            </button>

            {/* Capture button */}
            <button
              onClick={capturePhoto}
              disabled={!cameraActive}
              className="w-20 h-20 rounded-full bg-white disabled:opacity-50 flex items-center justify-center"
            >
              <div className="w-16 h-16 rounded-full border-4 border-gray-800"></div>
            </button>

            {/* Upload button */}
            <button
              onClick={uploadPhotos}
              disabled={capturedPhotos.length === 0}
              className={`p-3 rounded-full ${
                capturedPhotos.length > 0 ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-500'
              }`}
            >
              <CheckIcon className="h-8 w-8" />
            </button>
          </div>
        )}
      </div>

      {/* Quick tips */}
      {cameraActive && capturedPhotos.length === 0 && (
        <div className="absolute bottom-32 left-0 right-0 text-center">
          <p className="text-white/70 text-sm px-4">
            Tip: Capture exterior from all angles, interior, dashboard, and any damage
          </p>
        </div>
      )}
    </div>
  );
};

export default MobilePhotoCapture;
